/**
 * The Captain roster station panel (M6 spec §5, §8, plan P27): this station's candidates, hired into the pool and,
 * when chosen, straight onto a captainless ship. Thin: the rules live in `models/roster.ts`. The assignment choice
 * cycles with its button (and left/right on the focused button).
 */
import type { ReactNode } from 'react'
import { FocusButton } from '../focus'
import type { RosterModel } from './models/roster'
import '../strategic/strategic.css'
import './stationPanels.css'

export interface RosterProps {
  /** The heading (panels.json `labels.roster.title`, "Hiring board"). */
  title: string
  model: RosterModel
  status: string | null
  busy: boolean
  onHire: (candidate: number) => void
  onAssign: (delta: 1 | -1) => void
}

export function Roster(p: RosterProps): ReactNode {
  const m = p.model
  return (
    <div className="sui fp-panel" data-testid="fleet-roster">
      <header className="sui-head">
        <h2 className="sui-title">{p.title}</h2>
        <p className="sui-sub">{m.wageNote}</p>
      </header>
      <p className="fp-meta">
        <span data-testid="roster-hired">{m.hiredLabel}</span>
      </p>
      <div className="fp-row">
        <div>
          <div className="fp-row__sub">Assign the next hire to</div>
          <div className="fp-row__name" data-testid="roster-assign-label">
            {m.assignLabel}
          </div>
        </div>
        <div />
        <div className="fp-row__actions">
          <FocusButton group="roster" index={0} className="ui-btn--small" testId="roster-assign" disabled={m.targets.length === 0 || p.busy} onActivate={() => p.onAssign(1)} onAdjust={p.onAssign}>
            Change ship
          </FocusButton>
        </div>
      </div>
      {m.empty !== null ? <p className="sui-empty">{m.empty}</p> : null}
      {m.rows.map((r, i) => (
        <div key={r.id} className="fp-row" data-testid={`roster-row-${r.candidate}`}>
          <div>
            <div className="fp-row__name">{r.name}</div>
            <div className="fp-row__sub">{r.skills}</div>
            <div className="fp-row__sub">{r.traits}</div>
          </div>
          <div className="fp-row__price ui-num">{r.wageLabel}</div>
          {r.reason !== '' ? <div className="fp-row__reason">{r.reason}</div> : null}
          <div className="fp-row__actions">
            <FocusButton group="roster" index={i + 1} className="ui-btn--small" testId={`roster-hire-${r.candidate}`} disabled={!r.canHire || p.busy} onActivate={() => p.onHire(r.candidate)}>
              Hire {r.name}
            </FocusButton>
          </div>
        </div>
      ))}
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="roster-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
